// Source : https://github.com/thednp/position-observer/blob/main/src/index.ts

type PositionObserverCallback = (
  entries: IntersectionObserverEntry[],
  observer: PositionObserver
) => void;

const callbackModes = ['all', 'intersecting', 'update'] as const;
type CallbackMode = (typeof callbackModes)[number];
type CallbackModeIndex = 0 | 1 | 2;

type PositionObserverOptions = {
  rootMargin?: IntersectionObserverInit['rootMargin'];
  threshold?: IntersectionObserverInit['threshold'];
  callbackMode?: CallbackMode;
};

/**
 * The PositionObserver class is a utility class that observes the position
 * of DOM elements and triggers a callback when their position changes.
 */
export class PositionObserver {
  public entries: Map<Element, IntersectionObserverEntry>;
  /** `PositionObserver.tick` */
  protected _t: number;
  /** `PositionObserver.root` */
  protected _r: Element;
  /** `PositionObserver.callbackMode` */
  protected _cm: CallbackModeIndex;
  /** `PositionObserver.root.clientWidth` */
  protected _w: number;
  /** `PositionObserver.root.clientHeight` */
  protected _h: number;
  /** `IntersectionObserver.options.rootMargin` */
  protected _rm: string | undefined;
  /** `IntersectionObserver.options.threshold` */
  protected _th: number | number[] | undefined;
  /** `PositionObserver.callback` */
  protected _c: PositionObserverCallback;

  /**
   * The constructor takes two arguments, a `callback`, which is called
   * whenever the position of an observed element changes and an `options` object.
   * The callback function takes an array of `PositionObserverEntry` objects
   * as its first argument and the PositionObserver instance as its second argument.
   *
   * @param callback the callback that applies to all targets of this observer
   * @param options the options of this observer
   */
  constructor(callback: PositionObserverCallback, options?: PositionObserverOptions) {
    this.entries = new Map<Element, IntersectionObserverEntry>();
    this._c = callback;
    this._t = 0;
    const root = document?.documentElement;
    this._r = root;
    this._rm = options?.rootMargin;
    this._th = options?.threshold;
    /* istanbul ignore next @preserve */
    this._cm = callbackModes.indexOf(options?.callbackMode || 'intersecting') as CallbackModeIndex;
    this._w = root.clientWidth;
    this._h = root.clientHeight;
  }

  /**
   * Start observing the position of the specified element.
   * If the element is not currently attached to the DOM,
   * it will NOT be added to the entries.
   *
   * @param target an `Element` target
   */
  public observe = (target: Element) => {
    /* istanbul ignore else @preserve - a guard must be set */
    if (!this._r.contains(target)) return;

    // define a new entry
    // push the entry into the queue
    this._n(target).then(ioEntry => {
      /* istanbul ignore else @preserve - don't allow duplicate entries */
      if (ioEntry.boundingClientRect && !this.getEntry(target)) {
        this.entries.set(target, ioEntry);
      }

      /* istanbul ignore else @preserve */
      if (!this._t) this._t = requestAnimationFrame(this._rc);
    });
  };

  /**
   * Stop observing the position of the specified element.
   *
   * @param target an `Element` target
   */
  public unobserve = (target: Element) => {
    /* istanbul ignore else @preserve */
    if (this.entries.has(target)) this.entries.delete(target);
  };

  /**
   * Private method responsible for all the heavy duty,
   * the observer's runtime.
   * `PositionObserver.runCallback`
   */
  protected _rc = () => {
    /* istanbul ignore if @preserve - a guard must be set */
    if (!this.entries.size) {
      this._t = 0;
      return;
    }
    const { clientWidth, clientHeight } = this._r;

    const queue = new Promise<IntersectionObserverEntry[]>(resolve => {
      const updates: IntersectionObserverEntry[] = [];
      this.entries.forEach(
        ({ target, boundingClientRect: oldBoundingBox, isIntersecting: oldIsIntersecting }) => {
          /* istanbul ignore if @preserve - a guard must be set when target has been removed */
          if (!this._r.contains(target)) return;

          this._n(target).then(ioEntry => {
            /* istanbul ignore if @preserve - make sure to only count visible entries */
            if (!ioEntry.isIntersecting) {
              if (this._cm === 1) {
                // 1 = "intersecting"
                return;
              } else if (this._cm === 2) {
                // 2 = "update"
                if (oldIsIntersecting) {
                  this.entries.set(target, ioEntry);
                  updates.push(ioEntry);
                }
                return;
              }
            }
            // 0 = "all"
            const { left, top } = ioEntry.boundingClientRect;

            /* istanbul ignore else @preserve - only schedule entries that changed position */
            if (
              oldBoundingBox.top !== top ||
              oldBoundingBox.left !== left ||
              this._w !== clientWidth ||
              this._h !== clientHeight
            ) {
              this.entries.set(target, ioEntry);
              updates.push(ioEntry);
            }
          });
        }
      );
      // update root client width & height
      this._w = clientWidth;
      this._h = clientHeight;

      resolve(updates);
    });

    this._t = requestAnimationFrame(async () => {
      // execute the queue
      const updates: IntersectionObserverEntry[] = await queue;

      // only execute the callback if position actually changed
      /* istanbul ignore else @preserve */
      if (updates.length) this._c(updates, this);

      this._rc();
    });
  };

  /**
   * Check intersection status and resolve it
   * right away.
   *
   * `PositionObserver.newEntryForTarget`
   *
   * @param target an `Element` target
   */
  protected _n = (target: Element) => {
    return new Promise<IntersectionObserverEntry>(resolve => {
      const intersectionObserver = new IntersectionObserver(
        ([ioEntry], ob) => {
          ob.disconnect();
          resolve(ioEntry);
        },
        {
          // IntersectionObserver.root is always the document
          // root: target.ownerDocument || document,
          threshold: this._th,
          rootMargin: this._rm,
        }
      );

      intersectionObserver.observe(target);
    });
  };

  /**
   * Find the entry for a given target.
   *
   * @param target an `HTMLElement` target
   */
  public getEntry = (target: Element) => this.entries.get(target);

  /**
   * Immediately stop observing all elements.
   */
  public disconnect = () => {
    cancelAnimationFrame(this._t);
    this.entries.clear();
    this._t = 0;
  };
}
